/**
 * Main hook for the ThemeOverride integrated component.
 *
 * Resolves the current level's config into CSS custom properties, then merges
 * with any ancestor ThemeOverride overrides so that nested scopes compose
 * correctly.
 */

import { useMemo } from 'react';
import type * as React from 'react';

import type {
  ThemeOverrideConfig,
  UseThemeOverrideReturn,
} from '../types';
import { buildThemeStyle } from '../utils/buildThemeStyle';
import { mergeThemeOverrides } from '../utils/mergeThemeOverrides';
import { useThemeOverrideContext } from '../context/ThemeOverrideContext';

// ---------------------------------------------------------------------------
// Public Types
// ---------------------------------------------------------------------------

export interface UseThemeOverrideHookReturn extends UseThemeOverrideReturn {
  /** Merged flat override map (for context propagation). */
  activeOverrides: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Compute the CSS custom properties for a ThemeOverride scope.
 *
 * 1. Reads the outer (ancestor) context, if any.
 * 2. Builds this level's override map from `config`.
 * 3. Merges outer + current (current wins on conflicts).
 * 4. Returns the merged `styleOverrides` (for inline style), the resolved
 *    `config`, and the merged `activeOverrides` (for context propagation).
 */
export function useThemeOverride(
  config: ThemeOverrideConfig,
): UseThemeOverrideHookReturn {
  const outerContext = useThemeOverrideContext();

  return useMemo(() => {
    const { overrideMap } = buildThemeStyle(config);

    const mergedOverrides = outerContext
      ? mergeThemeOverrides(outerContext.activeOverrides, overrideMap)
      : overrideMap;

    return {
      styleOverrides: mergedOverrides as React.CSSProperties,
      config,
      activeOverrides: mergedOverrides,
    };
  }, [config, outerContext]);
}
